import { useState } from 'react'
import { MessageFeed } from './chat/MessageFeed'
import { MessageInput } from './chat/MessageInput'
import { useVTTStore } from '@/store'
import type { VTTExpectedPlayer } from '@/services/sessionMetaService'

const ROSTER_COLLAPSED_KEY = 'vtt:roster-collapsed'

interface Participant {
  userId: string
  displayName: string
  role: string
}

interface ChatPanelProps {
  participants: Participant[]
  /** Expected-player roster from session metadata. Merged with the
   *  live socket participants so the chat-tab shows GREYED-OUT rows
   *  for players who haven't joined yet, plus a per-player flag when
   *  the player has no APPROVED character on the linked game. */
  expectedPlayers?: VTTExpectedPlayer[]
  currentUserId: string
}

type RosterRow = {
  userId: string
  displayName: string
  role: string
  online: boolean
  hasCharacter?: boolean
  avatarUrl?: string | null
}

const AVATAR_COLORS = [
  'hsl(43 70% 55%)',  // amber
  'hsl(210 80% 58%)', // blue
  'hsl(280 60% 60%)', // purple
  'hsl(330 65% 55%)', // rose
  'hsl(150 65% 48%)', // green
  'hsl(0 60% 55%)',   // red
]

function avatarColor(userId: string, role: string): string {
  if (role === 'gm') return 'hsl(var(--ac-mint-primary))'
  let h = 0
  for (let i = 0; i < userId.length; i++) {
    h = ((h << 5) - h + userId.charCodeAt(i)) | 0
  }
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

function roleLabel(role: string): string {
  if (role === 'gm') return 'GM'
  if (role === 'observer') return 'Observer'
  return 'Player'
}

/**
 * v5 chat tab — three stacked surfaces:
 *
 *   1. "Around the table" roster — flat panel of `.ac-roster-row`
 *      entries with online pips. Anyone in the websocket
 *      participants list shows as online; away/offline states are
 *      placeholders for future presence work.
 *   2. Chat & dice card — header strip ("Chat & dice" + command
 *      hints) over the scrolling MessageFeed.
 *   3. MessageInput — fixed to the bottom with composer + tip strip.
 */
export function ChatPanel({
  participants,
  expectedPlayers,
  currentUserId,
}: ChatPanelProps) {
  const messages = useVTTStore((state) => state.messages)

  // Roster collapse state. Persisted to sessionStorage so toggling
  // closed during a session sticks across re-renders / tab switches
  // but resets on a fresh launch (the roster header is the new-arrival
  // signal, so showing it open by default after a reconnect is right).
  const [rosterCollapsed, setRosterCollapsed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(ROSTER_COLLAPSED_KEY) === '1'
    } catch {
      return false
    }
  })
  const toggleRoster = () => {
    setRosterCollapsed((prev) => {
      const next = !prev
      try {
        sessionStorage.setItem(ROSTER_COLLAPSED_KEY, next ? '1' : '0')
      } catch {
        // sessionStorage unavailable — toggle still works in-memory
      }
      return next
    })
  }

  // Merge live socket participants with the expected-player roster
  // from session metadata. Connected players win on role; absent
  // expected players are appended as offline rows with the
  // hasCharacter flag carried through.
  const rosterRows: RosterRow[] = (() => {
    const connectedIds = new Set(participants.map((p) => p.userId))
    const expectedById = new Map(
      (expectedPlayers ?? []).map((p) => [p.userId, p]),
    )

    const connectedRows: RosterRow[] = participants.map((p) => ({
      userId: p.userId,
      displayName: p.displayName,
      role: p.role,
      online: true,
      hasCharacter: expectedById.get(p.userId)?.hasCharacter,
      avatarUrl: expectedById.get(p.userId)?.avatarUrl ?? null,
    }))

    const absentRows: RosterRow[] = (expectedPlayers ?? [])
      .filter((p) => !connectedIds.has(p.userId))
      .map((p) => ({
        userId: p.userId,
        displayName: p.displayName,
        role: 'player', // expected players from bookings are never GMs here
        online: false,
        hasCharacter: p.hasCharacter,
        avatarUrl: p.avatarUrl,
      }))

    return [...connectedRows, ...absentRows].sort((a, b) => {
      if (a.userId === currentUserId) return -1
      if (b.userId === currentUserId) return 1
      // Connected > offline
      if (a.online !== b.online) return a.online ? -1 : 1
      return 0
    })
  })()

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Roster */}
      <section
        className="ac-card"
        style={{
          background: 'hsl(var(--ac-dark-secondary) / 0.5)',
          padding: '0.625rem',
        }}
      >
        <button
          type="button"
          onClick={toggleRoster}
          aria-expanded={!rosterCollapsed}
          aria-controls="around-the-table-list"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: '0 0.375rem 0.375rem',
            background: 'transparent',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            font: 'inherit',
            textAlign: 'left',
          }}
        >
          <span
            className="font-script"
            style={{
              fontSize: '0.6875rem',
              letterSpacing: '0.25em',
              textTransform: 'uppercase',
              color: 'hsl(var(--ac-mint-primary))',
            }}
          >
            Around the table
          </span>
          <span
            className="ac-help"
            style={{
              fontSize: '0.625rem',
              color: 'hsl(var(--ac-text-muted))',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {rosterRows.length}
            <svg
              aria-hidden
              width={10}
              height={10}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                transition: 'transform 150ms var(--ac-ease-out, ease-out)',
                transform: rosterCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
              }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>
        <div id="around-the-table-list" hidden={rosterCollapsed}>
          {rosterRows.map((p) => {
            const isSelf = p.userId === currentUserId
            const isGM = p.role === 'gm'
            const pipClass = p.online ? 'pip is-online' : 'pip is-offline'
            return (
              <div
                key={p.userId}
                className={`ac-roster-row ${isSelf ? 'is-self' : ''}`}
                style={{ opacity: p.online ? 1 : 0.6 }}
              >
                <span className={pipClass} />
                <span
                  className={isGM ? 'vtt-gm-ring' : undefined}
                  style={isGM ? undefined : { display: 'inline-flex' }}
                >
                  <span
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: avatarColor(p.userId, p.role),
                      color: isGM ? 'hsl(0 0% 10%)' : 'hsl(0 0% 98%)',
                      fontFamily: 'var(--ac-font-serif)',
                      fontSize: 12,
                      fontWeight: 500,
                      filter: p.online ? undefined : 'grayscale(0.4)',
                    }}
                  >
                    {p.displayName.charAt(0).toUpperCase()}
                  </span>
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: '0.8125rem',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {p.displayName}
                    </span>
                    {isSelf && (
                      <span
                        style={{
                          color: 'hsl(var(--ac-mint-primary))',
                          fontSize: '0.625rem',
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        (you)
                      </span>
                    )}
                    {p.hasCharacter === false && (
                      <span
                        title="No approved character on this game"
                        aria-label="No approved character"
                        style={{
                          color: 'hsl(var(--ac-warning))',
                          fontSize: '0.6875rem',
                          flexShrink: 0,
                        }}
                      >
                        ⚠
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: '0.625rem',
                      color: isGM
                        ? 'hsl(var(--ac-gm-gold))'
                        : 'hsl(var(--ac-text-muted))',
                    }}
                  >
                    {p.online ? roleLabel(p.role) : 'Expected · offline'}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* Chat & dice card */}
      <section
        className="ac-card"
        style={{
          background: 'hsl(var(--ac-dark-primary) / 0.5)',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '0.5rem 0.75rem',
            borderBottom: '1px solid hsl(var(--ac-dark-accent))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem',
          }}
        >
          <div
            className="font-serif-ac"
            style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}
          >
            Chat &amp; dice
          </div>
          <div
            className="font-mono-ac"
            style={{
              fontSize: '0.625rem',
              color: 'hsl(var(--ac-text-muted))',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            /r · /w · /gmroll
          </div>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            padding: '0.5rem',
            display: 'flex',
          }}
        >
          <MessageFeed messages={messages} currentUserId={currentUserId} />
        </div>

        <MessageInput participants={participants} currentUserId={currentUserId} />
      </section>
    </div>
  )
}
